import moment from "moment-timezone";
import { LIMITES, TIPO, VENTA_MESA, TIPO_COMPLEMENTO, COLOR_POR_DEFECTO, preguntaVacia, tarifaVacia } from "./schema";

const ZONA = "America/Argentina/Buenos_Aires";
const FORMATO_API = "YYYY-MM-DDTHH:mm:ssZ";
const FORMATO_INPUT = "YYYY-MM-DDTHH:mm";

export const aInstante = (valor) => moment.tz(valor, FORMATO_INPUT, ZONA);
export const aInput = (valor) => (valor ? moment(valor).tz(ZONA).format(FORMATO_INPUT) : "");

const entero = (valor, porDefecto = 0) => {
  const n = parseInt(valor, 10);
  return Number.isNaN(n) ? porDefecto : n;
};

export const preguntasAEnviar = (form) =>
  form.informacion_adicional
    ? form.preguntas
        .map((p) => ({ informacion_adicional: p.informacion_adicional.trim(), obligatoria: p.obligatoria !== false }))
        .filter((p) => p.informacion_adicional !== "")
    : [];

// mesaAEnviar: la venta por mesa sólo existe en tickets; por silla no lleva
// paquetes ni complementos
const mesaAEnviar = (form, esTicket) => {
  if (!esTicket) return {};
  if (form.venta_mesa !== VENTA_MESA.MESA) return { venta_mesa: VENTA_MESA.SILLA, paquetes: [], complementos: [] };
  return {
    venta_mesa: VENTA_MESA.MESA,
    paquetes: form.paquetes.map((p) => ({ id: p.id || null, personas: entero(p.personas), precio: Number(p.precio) || 0 })),
    complementos: form.complementos
      .filter((c) => c.idtarifa)
      .map((c) => ({
        idtarifa: Number(c.idtarifa),
        tipo: c.tipo,
        maximo_por_mesa: c.tipo === TIPO_COMPLEMENTO.ADICIONAL ? entero(c.maximo_por_mesa) : null,
      })),
  };
};

export const toPayload = (form, { eventoId }) => {
  const esTicket = form.idtipo === TIPO.TICKET;
  const nuevaUbicacion = esTicket && form.nueva_ubicacion;
  return {
    idevento: Number(eventoId),
    idtipo: form.idtipo,
    formato: form.formato,
    copiar_diseno_de: form.copiar_diseno_de,
    concepto: form.concepto.trim(),
    descripcion: form.descripcion.trim(),
    precio: Number(form.precio) || 0,
    privado: form.privado,
    // en una privada el backend usa desde ahora hasta el fin del evento
    inicio_venta: !form.privado && form.inicio_venta ? aInstante(form.inicio_venta).format(FORMATO_API) : null,
    fin_venta: !form.privado && form.fin_venta ? aInstante(form.fin_venta).format(FORMATO_API) : null,
    fechas: form.fechas.map((id) => ({ id: Number(id) })),
    // el backend crea la ubicación nueva en la misma transacción (o reusa una con ese nombre)
    idubicacion: esTicket && !nuevaUbicacion ? Number(form.idubicacion) : null,
    ubicacion_nueva: nuevaUbicacion
      ? {
          nombre: form.nombre_ubicacion.trim(),
          limite_persona: entero(form.limite_persona),
          limite_maximo: entero(form.limite_maximo),
        }
      : null,
    idtarifa_previa: esTicket && !form.privado && form.por_tandas && form.idtarifa_previa ? Number(form.idtarifa_previa) : null,
    limite_persona: esTicket ? null : entero(form.limite_persona),
    limite_maximo: esTicket ? null : entero(form.limite_maximo),
    asumir_comision: Number(form.precio) > 0 && form.asumir_comision,
    requiere_aprobacion: !(Number(form.precio) > 0) && form.requiere_aprobacion,
    informacion_adicional: form.informacion_adicional,
    adicionales: preguntasAEnviar(form),
    preimpreso: form.preimpreso,
    consalidas: form.consalidas,
    imprimeqr: form.imprimeqr,
    unidadstock: entero(form.unidadstock, 1) || 1,
    color: form.color || COLOR_POR_DEFECTO,
    ...mesaAEnviar(form, esTicket),
  };
};

export const fromTarifa = (tarifa) => {
  const base = tarifaVacia(tarifa.idtipo, { hayUbicaciones: true });
  return {
    ...base,
    id: tarifa.id,
    concepto: tarifa.concepto ?? "",
    descripcion: tarifa.descripcion ?? "",
    precio: tarifa.precio ?? "",
    privado: tarifa.privado === true,
    inicio_venta: aInput(tarifa.inicio_venta),
    fin_venta: aInput(tarifa.fin_venta),
    fechas: (tarifa.fechas ?? []).map((f) => f.id_evento_fecha ?? f.fecha?.id).filter(Boolean),
    idubicacion: tarifa.idubicacion ?? "",
    nueva_ubicacion: false,
    por_tandas: Boolean(tarifa.idtarifa_previa),
    idtarifa_previa: tarifa.idtarifa_previa ?? "",
    limite_persona: tarifa.limite_persona ?? base.limite_persona,
    limite_maximo: tarifa.limite_maximo ?? base.limite_maximo,
    asumir_comision: tarifa.asumir_comision === true,
    requiere_aprobacion: tarifa.requiere_aprobacion === true,
    informacion_adicional: tarifa.informacion_adicional === true,
    preguntas: tarifa.adicionales?.length
      ? tarifa.adicionales.map((a) => ({ informacion_adicional: a.informacion_adicional, obligatoria: a.obligatoria !== false }))
      : [preguntaVacia()],
    preimpreso: tarifa.preimpreso === true,
    consalidas: tarifa.consalidas === true,
    imprimeqr: tarifa.imprimeqr !== false,
    unidadstock: tarifa.unidadstock || 1,
    color: tarifa.color || COLOR_POR_DEFECTO,
    venta_mesa: tarifa.mesa?.venta_mesa || VENTA_MESA.SILLA,
    paquetes: (tarifa.mesa?.paquetes ?? []).map((p) => ({ id: p.id, personas: p.personas, precio: p.precio })),
    complementos: (tarifa.mesa?.complementos ?? []).map((c) => ({
      idtarifa: c.idtarifa_complemento,
      tipo: c.tipo,
      maximo_por_mesa: c.maximo_por_mesa ?? "",
    })),
  };
};

// una tarifa nueva igual a otra (por ejemplo, la próxima tanda de venta), con
// su mismo diseño de impresión
export const duplicarTarifa = (tarifa) => ({
  ...fromTarifa(tarifa),
  id: null,
  // cada tarifa tiene una sola siguiente: la copia arranca sin tanda
  por_tandas: false,
  idtarifa_previa: "",
  concepto: `${tarifa.concepto} (copia)`.slice(0, LIMITES.CONCEPTO_MAX),
  copiar_diseno_de: tarifa.id,
  paquetes: fromTarifa(tarifa).paquetes.map((p) => ({ ...p, id: null })),
});
